import React, { useEffect, useState } from "react"
import _ from "lodash"
import { useSelector, useDispatch } from "react-redux"
import { useFormik } from "formik"
import { useTranslation } from "react-i18next"
import {
	Box,
	FormControl,
	TextField,
	makeStyles,
	Button,
	Typography,
	Select,
	MenuItem,
	FormHelperText,
	InputAdornment,
	IconButton,
	Tooltip,
	InputLabel,
	Input,
	Checkbox,
	ListItemText,
	Radio,
	RadioGroup,
	FormControlLabel,
	FormLabel,
} from "@material-ui/core"
import { useTheme } from "@material-ui/core/styles"
import * as yup from "yup"
import { KeyboardArrowDown } from "@material-ui/icons"
import VisibilityOffSVG from "../icons/VisibilityOffSVG"
import VisibilitySVG from "../icons/VisibilitySVG"
import InfoIconSVG from "../icons/InfoIconSVG"
import { roles } from "../../data/roles"
import { createUser, emailAvailabilityCheck, updateUser, updateUserReset } from "../../actions/usersActions"
import { validateEmail } from "../../utils"

//const nameRegx = /^[A-Za-z][A-Za-z\'\-]+([\ A-Za-z][A-Za-z\'\-]+)*/
//const nameRegx = /^[A-Za-zÆØÅæøåÄ][A-Za-z\'\-\Wæøå]+([\ A-Za-z][A-Za-z\'\-\Wæøå]+)*/

const nameRegx = /^\S+$/
const useStyles = makeStyles((theme) => ({
	root: {
		display: "flex",
		flexWrap: "wrap",
	},
	margin: {
		marginBottom: theme.spacing(3),
		"& .Mui-error": {
			marginLeft: 0,
			marginRight: 0,
		},
		"& .MuiFormLabel-root": {
			fontSize: theme.typography.fontSize,
		},
		"& .MuiFormHelperText-contained": {
			marginLeft: 0,
			marginRight: 0,
			color: theme.palette.error.main,
		},
	},
	dividerLine: {
		border: `1px solid ${theme.palette.grey[400]}`,
		marginTop: 0,
	},
	formActions: {
		display: "flex",
		justifyContent: "flex-end",
		flexDirection: "row",
		alignItems: "center",
	},
	formActionsBtn: {
		...theme.typography.fieldValue,
		fontWeight: 500,
		margin: "8px",
		padding: "4px 12px",
		textTransform: "capitalize",
	},
	required: {
		color: theme.palette.error.main,
	},
	textLabel: {
		...theme.typography.fieldValue,
		paddingBottom: 10,
	},
	textField: {
		"& .MuiOutlinedInput-input": {
			//padding: "12px 13px",
		},
		"& fieldset > legend": {
			float: "none",
		},
	},
	userRoleDropdown: {
		display: "flex",
		flexDirection: "row",
		alignItems: "flex-end",
		padding: "2px",
		gap: "2px",
		borderRadius: "3px",
		fontSize: theme.typography.fontSize,
		"& .MuiSelect-icon": {
			color: theme.palette.grey["D300"],
		},
		"& .MuiInput-input": {
			padding: "12px 13px",
		},
		"& fieldset > legend": {
			float: "none",
		},
		maxWidth: "567px",
	},
	menuItemStyle: {
		borderBottom: `1px solid ${theme.palette.grey["P300"]}`,
	},
	activeBtn: {
		...theme.typography.fieldValue,
		fontWeight: 500,
		margin: "8px",
		padding: "4px 12px",
		textTransform: "capitalize",
		background: theme.palette.primary.dark,
		color: theme.palette.common.white,
		"&:hover": {
			background: theme.palette.primary.dark,
		},
	},
	passwordChange: {
		"& .MuiRadio-colorSecondary.Mui-checked": {
			color: theme.palette.primary.dark,
		},
		"& .MuiTypography-body1": {
			fontSize: theme.typography.fontSize,
		},
		"& .MuiFormControlLabel-root": {
			marginLeft: 0,
		},
	},
}))

const ITEM_HEIGHT = 58
const ITEM_PADDING_TOP = 8

function AddUser(props) {
	const theme = useTheme()
	const classes = useStyles()
	const [t] = useTranslation("global")
	const dispatch = useDispatch()
	const [showPassword, setShowPassword] = useState(false)
	const [showConfirmPassword, setShowConfirmPassword] = useState(false)
	const [passwordChangeRequest, setPasswordChangeRequest] = useState(false)
	const userRoles = Object.keys(roles)
	const { multiBaseList } = useSelector((state) => state.multiBase)
	const { user } = useSelector((state) => state?.authtentication?.user)
	const { isUserCreated, isEmailAvailaible, isUserUpdated, caseList } = useSelector((state) => state.userManagement)

	const handelEmailChange = _.debounce((e) => {
		let trimedEmail = e?.target?.value?.trim()
		if (trimedEmail?.length > 0) {
			if (validateEmail(trimedEmail)) {
				dispatch(emailAvailabilityCheck({ email: trimedEmail }))
			}
		}
	}, 500)

	const getUserSpecificRoles = () => {
		let loggedInUserRoles = []
		if (user?.roles?.length > 0) {
			if (user?.roles?.includes(roles?.Super_User)) {
				loggedInUserRoles = Object?.keys(roles)
			} else if (user?.roles?.includes(roles?.System_Administrator)) {
				loggedInUserRoles?.push(roles?.Case_Administrator, roles?.Case_User)
			}
		}
		return loggedInUserRoles
	}

	const passwordInitailValues =
		props?.isAddMode || passwordChangeRequest
			? {
					password: "",
					confirmPassword: "",
			  }
			: {}

	const passwordValidationSchema =
		props?.isAddMode || passwordChangeRequest
			? {
					password: yup
						.string()
						.required(t("user.formErrors.required"))
						.matches(
							/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[(!@#$%^&*.)])[A-Za-z\d(!@#$%^&*.)]{8,20}$/,
							t("user.formErrors.password")
						),
					confirmPassword: yup
						.string()
						.required(t("user.formErrors.required"))
						.oneOf([yup.ref("password"), null], t("user.formErrors.passwordMustMatch")),
			  }
			: {}

	const emailValidationSchema = props?.isAddMode
		? {
				email: yup
					.string()
					.trim()
					.email(t("user.formErrors.email"))
					.required(t("user.formErrors.required"))
					.test("Unique Email", t("user.formErrors.emailAlreadyExist"), () => {
						return isEmailAvailaible
					}),
		  }
		: {
				email: yup.string().email(t("user.formErrors.email")).required(t("user.formErrors.required")),
		  }

	const userValidationSchema = yup.object({
		first_name: yup.string().trim().max(30, t("user.formErrors.max")).required(t("user.formErrors.required")),
		last_name: yup.string().trim().max(30, t("user.formErrors.max")).required(t("user.formErrors.required")),
		roles: yup.array().of(yup.string().required(t("user.formErrors.required"))),
		projects: yup.array().of(yup.string()),
		...emailValidationSchema,
		...passwordValidationSchema,
	})

	const handleFormSubmit = (formData) => {
		if (formData?.roles?.length > 0 && validateUserFirstAndLastName(formData)) {
			props?.isAddMode ? addUserData(formData) : updateUserData(formData)
		} else {
			addUserForm?.setFieldError("roles", t("user.formErrors.required"))
		}

		!isEmailAvailaible && props?.isAddMode ? addUserForm?.setFieldError("email", t("user.formErrors.emailAlreadyExist")) : ""
	}

	const validateUserFirstAndLastName = (formData) => {
		if (formData?.first_name?.trim()?.length > 0 && formData?.last_name?.trim()?.length > 0) {
			return true
		} else {
			if (!formData?.first_name?.trim()?.length) {
				addUserForm?.setFieldError("first_name", t("user.formErrors.firstName"))
			} else {
				addUserForm?.setFieldError("last_name", t("user.formErrors.lastName"))
			}
		}
	}

	const addUserData = (data) => {
		dispatch(
			createUser({
				...data,
				first_name: data?.first_name?.trim(),
				last_name: data?.last_name?.trim(),
				active: true,
			})
		)
	}

	const getChangedValues = (values, initialValues) => {
		return Object.entries(values)?.reduce((acc, [key, value]) => {
			const hasChanged = initialValues?.[key] !== value
			if (hasChanged) {
				if (key === "first_name" || key === "last_name") {
					acc[key] = value?.trim()
				} else {
					acc[key] = value
				}
			}
			return acc
		}, {})
	}

	const updateUserData = (data) => {
		const resultObject = getChangedValues(data, addUserForm?.initialValues)
		if (!_.isEmpty(resultObject)) {
			dispatch(updateUser({ ...resultObject, userid: props?.userData?.userid }))
		} else {
			props?.closeDialog(false)
			addUserForm?.resetForm()
		}
	}

	const addUserForm = useFormik({
		initialValues: {
			first_name: props?.userData?.first_name || "",
			last_name: props?.userData?.last_name || "",
			email: props?.userData?.email || "",
			roles: props?.userData?.roles || [],
			projects: props?.userData?.projects || [],
			...passwordInitailValues,
		},
		validationSchema: userValidationSchema,
		onSubmit: handleFormSubmit,
	})

	const getProjectNameList = (projectIds) => {
		let projectList = []
		if (projectIds?.length > 0) {
			for (let i = 0; i < projectIds?.length; i++) {
				if (caseList?.length > 0) {
					const found = caseList.filter((project) => project?.project_id === projectIds[i])
					found?.[0]?.friendly_name ? projectList.push(found?.[0]?.friendly_name) : ""
				}
			}
		}
		return projectList
	}

	const handlePasswordChange = (e) => {
		if (e?.target?.value === "yes") {
			setPasswordChangeRequest(true)
		} else {
			setPasswordChangeRequest(false)
			addUserForm?.resetForm({ password: "", confirmPassword: "" })
		}
	}

	useEffect(() => {
		if (isUserCreated && props?.isAddMode) {
			addUserForm?.resetForm()
			props?.closeDialog(isUserCreated)
		} else if (isUserUpdated && !props?.isAddMode) {
			addUserForm?.resetForm()
			props?.closeDialog(isUserUpdated)
		}
	}, [isUserCreated, isUserUpdated])

	useEffect(() => {
		if (props?.isAddMode) {
			if (isEmailAvailaible) {
				addUserForm?.setFieldError("email", "")
			} else {
				addUserForm?.setFieldError("email", t("user.formErrors.emailAlreadyExist"))
			}
		}
	}, [isEmailAvailaible])

	useEffect(() => {
		return () => {
			if (!props?.isAddMode) dispatch(updateUserReset())
		}
	}, [])

	return (
		<form className={classes.root} noValidate autoComplete="off" id="create-user" onSubmit={addUserForm.handleSubmit}>
			<Box>
				<FormControl fullWidth className={classes.margin}>
					<TextField
						id="first_name"
						label={`${t("user.formFields.firstName")}*`}
						variant="outlined"
						placeholder={t("user.formFields.firstName")}
						className={classes.textField}
						value={addUserForm.values.first_name}
						onChange={addUserForm.handleChange}
						onBlur={addUserForm.handleBlur}
						error={addUserForm.touched.first_name && Boolean(addUserForm.errors.first_name)}
						helperText={addUserForm.touched.first_name && addUserForm.errors.first_name}
					/>
				</FormControl>
				<FormControl fullWidth className={classes.margin}>
					<TextField
						id="last_name"
						variant="outlined"
						label={`${t("user.formFields.lastName")}*`}
						placeholder={t("user.formFields.lastName")}
						className={classes.textField}
						value={addUserForm.values.last_name}
						onChange={addUserForm.handleChange}
						onBlur={addUserForm.handleBlur}
						error={addUserForm.touched.last_name && Boolean(addUserForm.errors.last_name)}
						helperText={addUserForm.touched.last_name && addUserForm.errors.last_name}
					/>
				</FormControl>
				<FormControl fullWidth variant="outlined" className={classes.margin}>
					<InputLabel id="rolesLabel">{t("user.formFields.assignUserRole.label")}*</InputLabel>
					<Select
						id="roles"
						name="roles"
						labelId="rolesLabel"
						label={`${t("user.formFields.assignUserRole.label")}*`}
						variant="outlined"
						value={addUserForm.values.roles}
						onChange={(selectedOption) => {
							addUserForm.handleChange(selectedOption)
						}}
						onBlur={() => {
							addUserForm?.setFieldTouched("roles", true)
						}}
						displayEmpty
						className={classes.userRoleDropdown}
						MenuProps={{
							anchorOrigin: {
								vertical: "bottom",
								horizontal: "left",
							},
							getContentAnchorEl: null,
							PaperProps: {
								style: {
									maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
								},
							},
						}}
						multiple={true}
						renderValue={(selected) => {
							return selected.join(", ")
						}}
						IconComponent={KeyboardArrowDown}
					>
						{getUserSpecificRoles()?.map((role) => (
							<MenuItem key={role} value={role} className={classes.menuItemStyle}>
								<Checkbox checked={addUserForm?.values?.roles?.indexOf(role) > -1} />
								<ListItemText primary={t(`user.roles.${role}`)} />
							</MenuItem>
						))}
					</Select>
					{addUserForm.touched.roles && !addUserForm.values.roles?.length && (
						<FormHelperText style={{ marginTop: "2px" }}>{t("user.formErrors.required")}</FormHelperText>
					)}
				</FormControl>
				<FormControl variant="outlined" fullWidth className={classes.margin}>
					<InputLabel id="projectsLabel">{t("user.formFields.addCases.label")}</InputLabel>
					<Select
						id="projects"
						name="projects"
						labelId="projectsLabel"
						label={t("user.formFields.addCases.label")}
						variant="outlined"
						value={addUserForm.values.projects}
						onChange={(selectedOption) => {
							addUserForm.handleChange(selectedOption)
						}}
						displayEmpty
						className={classes.userRoleDropdown}
						MenuProps={{
							anchorOrigin: {
								vertical: "bottom",
								horizontal: "left",
							},
							getContentAnchorEl: null,
							PaperProps: {
								style: {
									maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
								},
							},
						}}
						multiple={true}
						renderValue={(selected) => {
							let selectedCaseList = getProjectNameList(selected)
							return selectedCaseList.join(", ")
						}}
						IconComponent={KeyboardArrowDown}
					>
						{caseList?.map((caseItem) => (
							<MenuItem key={caseItem?.project_id} value={caseItem?.project_id} className={classes.menuItemStyle}>
								<Checkbox checked={addUserForm?.values?.projects?.indexOf(caseItem?.project_id) > -1} />
								<ListItemText primary={caseItem?.friendly_name} />
							</MenuItem>
						))}
					</Select>
				</FormControl>
				<FormControl fullWidth className={classes.margin}>
					<TextField
						id="email"
						variant="outlined"
						label={`${t("user.formFields.email")}*`}
						placeholder={t("user.formFields.email")}
						className={classes.textField}
						value={addUserForm?.values?.email?.trim()}
						onChange={addUserForm.handleChange}
						onBlur={(e) => {
							handelEmailChange(e)
							addUserForm.handleBlur(e)
						}}
						error={props?.isAddMode && addUserForm.touched.email && Boolean(addUserForm.errors.email)}
						helperText={props?.isAddMode && addUserForm.touched.email && addUserForm.errors.email}
						disabled={!props?.isAddMode}
					/>
				</FormControl>
				{!props?.isAddMode && (
					<FormControl fullWidth className={classes.margin}>
						<Typography variant="body1" noWrap style={{ fontWeight: 500 }}>
							{t("user.formFields.resetPassword")}
						</Typography>
						<RadioGroup
							row
							name="changePassword"
							className={classes.passwordChange}
							defaultValue="no"
							onChange={handlePasswordChange}
						>
							<FormControlLabel value="yes" control={<Radio size="medium" />} label={t("user.formFields.radioLabelYes")} />
							<FormControlLabel value="no" control={<Radio size="medium" />} label={t("user.formFields.radioLabelNo")} />
						</RadioGroup>
					</FormControl>
				)}
				{(passwordChangeRequest || props?.isAddMode) && (
					<>
						<FormControl fullWidth className={classes.margin}>
							<TextField
								id="password"
								type={showPassword ? "text" : "password"}
								variant="outlined"
								label={`${t("user.formFields.password")}*`}
								placeholder={t("user.formFields.password")}
								className={classes.textField}
								value={addUserForm.values.password}
								onChange={addUserForm.handleChange}
								onBlur={addUserForm.handleBlur}
								error={addUserForm.touched.password && Boolean(addUserForm.errors.password)}
								helperText={addUserForm.touched.password && addUserForm.errors.password}
								InputProps={{
									endAdornment: (
										<InputAdornment position="end">
											<IconButton
												aria-label="toggle password visibility"
												onClick={() => setShowPassword(!showPassword)}
											>
												{showPassword ? <VisibilitySVG /> : <VisibilityOffSVG />}
											</IconButton>
										</InputAdornment>
									),
								}}
							/>
						</FormControl>
						<FormControl fullWidth className={classes.margin}>
							<TextField
								id="confirmPassword"
								type={showConfirmPassword ? "text" : "password"}
								label={`${t("user.formFields.confirmPassword")}*`}
								variant="outlined"
								placeholder={t("user.formFields.confirmPassword")}
								className={classes.textField}
								value={addUserForm.values.confirmPassword}
								onChange={addUserForm.handleChange}
								onBlur={addUserForm.handleBlur}
								error={addUserForm.touched.confirmPassword && Boolean(addUserForm.errors.confirmPassword)}
								helperText={addUserForm.touched.confirmPassword && addUserForm.errors.confirmPassword}
								InputProps={{
									endAdornment: (
										<InputAdornment position="end">
											<IconButton
												aria-label="toggle password visibility"
												onClick={() => setShowConfirmPassword(!showConfirmPassword)}
											>
												{showConfirmPassword ? <VisibilitySVG /> : <VisibilityOffSVG />}
											</IconButton>
										</InputAdornment>
									),
								}}
							/>
						</FormControl>
					</>
				)}
				<hr className={classes.dividerLine} />
				<FormControl className={classes.formActions}>
					<Button variant="contained" size="small" className={classes.formActionsBtn} onClick={() => props?.closeDialog()}>
						{t("user.formActions.cancel")}
					</Button>
					<Button
						variant="contained"
						size="small"
						className={addUserForm.isValid ? classes.activeBtn : classes.formActionsBtn}
						type="submit"
						disabled={!addUserForm.isValid}
					>
						{!props?.isAddMode ? t("user.formActions.update") : t("user.formActions.create")}
					</Button>
				</FormControl>
			</Box>
		</form>
	)
}

export default AddUser
